/**
 * API Client
 * Wrapper around Amplify REST API with automatic JWT authentication
 *
 * For local development, uses fetch() to call localhost backend
 * For production, uses Amplify API to call API Gateway
 */

import { get, post, put, del } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';
import { getRuntimeConfig } from '../config/runtimeConfig';

/**
 * API client name (matches amplify config)
 */
const API_NAME = 'CloakNextAPI';

/**
 * Base API path
 */
const BASE_PATH = '/api';

/**
 * Always use fetch directly — the Amplify REST API client does not automatically
 * attach the Cognito JWT for HTTP API Gateway with a Cognito JWT authorizer.
 * fetchApi() handles token attachment manually via fetchAuthSession() and works
 * correctly for both localhost and real API Gateway endpoints.
 */
const USE_FETCH = true;

/**
 * Get auth token from Amplify session
 */
async function getAuthToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() || null;
  } catch {
    return null;
  }
}

/**
 * API Error with response details
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public response?: any
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Make API request using fetch (for local development)
 */
async function fetchApi<T>(
  method: string,
  path: string,
  body?: any
): Promise<T> {
  const baseUrl = getRuntimeConfig().apiEndpoint;
  const url = `${baseUrl}${BASE_PATH}${path}`;

  try {
    const token = await getAuthToken();

    const response = await fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      ...(body && { body: JSON.stringify(body) }),
    });

    // Parse response
    const data = await response.json();

    if (!response.ok) {
      throw new ApiError(
        data.message || data.error || 'API request failed',
        response.status,
        data
      );
    }

    return data as T;
  } catch (error: any) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(error.message || 'Network error occurred');
  }
}

/**
 * GET request
 */
export async function apiGet<T>(path: string): Promise<T> {
  if (USE_FETCH) {
    return fetchApi<T>('GET', path);
  }

  // Use Amplify API for production
  const restOperation = get({
    apiName: API_NAME,
    path: `${BASE_PATH}${path}`,
  });
  const { body } = await restOperation.response;
  const data = await body.json();
  return data as T;
}

/**
 * POST request
 */
export async function apiPost<T>(path: string, body?: any): Promise<T> {
  if (USE_FETCH) {
    return fetchApi<T>('POST', path, body);
  }

  // Use Amplify API for production
  const restOperation = post({
    apiName: API_NAME,
    path: `${BASE_PATH}${path}`,
    options: {
      body: body ? JSON.stringify(body) : undefined,
    },
  });
  const { body: responseBody } = await restOperation.response;
  const data = await responseBody.json();
  return data as T;
}

/**
 * PUT request
 */
export async function apiPut<T>(path: string, body?: any): Promise<T> {
  if (USE_FETCH) {
    return fetchApi<T>('PUT', path, body);
  }

  // Use Amplify API for production
  const restOperation = put({
    apiName: API_NAME,
    path: `${BASE_PATH}${path}`,
    options: {
      body: body ? JSON.stringify(body) : undefined,
    },
  });
  const { body: responseBody } = await restOperation.response;
  const data = await responseBody.json();
  return data as T;
}

/**
 * DELETE request
 */
export async function apiDelete<T>(path: string): Promise<T> {
  if (USE_FETCH) {
    return fetchApi<T>('DELETE', path);
  }

  // Use Amplify API for production
  const restOperation = del({
    apiName: API_NAME,
    path: `${BASE_PATH}${path}`,
  });
  const { body: responseBody } = await restOperation.response;
  const data = await responseBody.json();
  return data as T;
}
